import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { WishlistProvider } from './context/WishlistContext';
import { CartProvider } from './context/CartContext';
import { SettingsProvider } from './context/SettingsContext';
import { CategoriesProvider } from './context/CategoriesContext';
import { ToastProvider } from './context/ToastContext';
import ToastContainer from './components/Toast/ToastContainer';
import CookieConsent from './components/CookieConsent';
import CartDrawer from './components/CartDrawer';
import reportWebVitals from './reportWebVitals';
import CareersPage from './pages/CareersPage';

const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProfileOverview = lazy(() => import('./pages/ProfileOverview'));
const ProfileOrders = lazy(() => import('./pages/ProfileOrders'));
const ProfileAddresses = lazy(() => import('./pages/ProfileAddresses'));
const ProfileWishlist = lazy(() => import('./pages/ProfileWishlist'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const SavedAddressesPage = lazy(() => import('./pages/SavedAddressesPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ShippingPolicyPage = lazy(() => import('./pages/ShippingPolicyPage'));
const ReturnsRefundsPage = lazy(() => import('./pages/ReturnsRefundsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const FAQsPage = lazy(() => import('./pages/FAQsPage'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const GiftCardPage = lazy(() => import('./pages/GiftCardPage'));
const ShopAllPage = lazy(() => import('./pages/ShopAllPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const RegisterEmailPage = lazy(() => import('./pages/RegisterEmailPage'));
const LoginEmailPage = lazy(() => import('./pages/LoginEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));

function LoadingFallback() {
  return (
    <div className='flex items-center justify-center min-h-screen'>
      <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <HelmetProvider>
    <React.StrictMode>
    <ToastProvider>
      <ToastContainer />
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <SettingsProvider>
              <CategoriesProvider>
                <Router>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path='/' element={<App />} />
                      <Route path='/shop-all' element={<ShopAllPage />} />
                      <Route path='/cart' element={<CartPage />} />
                      <Route path='/checkout' element={<CheckoutPage />} />
                      <Route path='/order-success' element={<OrderSuccessPage />} />
                      <Route path='/login' element={<LoginPage />} />
                      <Route path='/login/email' element={<LoginEmailPage />} />
                      <Route path='/register' element={<RegisterPage />} />
                      <Route path='/register/email' element={<RegisterEmailPage />} />
                      <Route path='/forgot-password' element={<ForgotPasswordPage />} />
                      <Route path='/reset-password' element={<ResetPasswordPage />} />
                      <Route path='/verify-email' element={<VerifyEmailPage />} />
                      <Route path='/profile' element={<ProfilePage />}>
                        <Route index element={<ProfileOverview />} />
                        <Route path='orders' element={<ProfileOrders />} />
                        <Route path='orders/:id' element={<OrderDetailPage />} />
                        <Route path='addresses' element={<ProfileAddresses />} />
                        <Route path='wishlist' element={<ProfileWishlist />} />
                        <Route path='settings' element={<ProfileSettings />} />
                      </Route>
                      <Route path='/orders' element={<OrdersPage />} />
                      <Route path='/saved-addresses' element={<SavedAddressesPage />} />
                      <Route path='/wishlist' element={<WishlistPage />} />
                      <Route path='/about-us' element={<AboutUsPage />} />
                      <Route path='/contact' element={<ContactPage />} />
                      <Route path='/shipping-policy' element={<ShippingPolicyPage />} />
                      <Route path='/returns-refunds' element={<ReturnsRefundsPage />} />
                      <Route path='/privacy-policy' element={<PrivacyPolicyPage />} />
                      <Route path='/terms-of-service' element={<TermsOfServicePage />} />
                       <Route path='/faqs' element={<FAQsPage />} />
                       <Route path='/careers' element={<CareersPage />} />
                        <Route path='/track-order' element={<TrackOrderPage />} />
                       <Route path='/order/track/:code' element={<TrackOrderPage />} />
                      <Route path='/gift-card' element={<GiftCardPage />} />
                      <Route path='/brands' element={<BrandsPage />} />
                      <Route path='/search' element={<SearchPage />} />
                       <Route path='/product/:id' element={<ProductDetailPage />} />
                       <Route path='/unsubscribe' element={<UnsubscribePage />} />
                       <Route path='*' element={<CategoryPage />} />
                    </Routes>
                    <CookieConsent />
                    <CartDrawer />
                  </Suspense>
                </Router>
              </CategoriesProvider>
            </SettingsProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </ToastProvider>
  </React.StrictMode>
  </HelmetProvider>
);

reportWebVitals();